import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { GlobalConstants } from 'src/app/services/global-constants';
import { AdditionTabs, TrainingDataTabs, SessionContentType } from '../training-page.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { DataService } from 'src/app/services/data.service';
import { PermissionHandlerService } from 'src/app/services/permission-handler.service';
import { TranslatorPipe } from 'src/app/services/translator.pipe';

@Component({
  selector: 'app-training-assessment',
  templateUrl: './training-assessment.component.html',
  styleUrls: ['./training-assessment.component.scss', '../training-page.component.scss']
})
export class TrainingAssessmentComponent {

  //Constants
  additionTabs = AdditionTabs;
  trainingDataTabs = TrainingDataTabs;
  contentType = SessionContentType;
  GlobalConstants = GlobalConstants;

  //Toogling Variables
  @Input() addNewAssessment: boolean = false;
  @Input() updateAssessmentData: any;
  @Input() trainingTypeId: string = "";
  selectedAssessment: any;

  //Data Variables
  @Input() courseId: string = '';
  @Input() courseData: any;

  assessments: any[] = [];
  startDate: Date = new Date('09-09-2023');
  today: Date = new Date();

  blockOptions: any[] = [
    { name: this.translate('Preview status'), value: 'Preview' },
    { name: this.translate('Block status'), value: 'Block' },
    { name: this.translate('Unblock status'), value: 'Unblock' }
  ];

  translate(value: string) {
    const pipe: TranslatorPipe = new TranslatorPipe();
    return pipe.transform(value);
  }

  //Input Variables

  //Output Variables
  @Output() sessionCreated: EventEmitter<boolean> = new EventEmitter()
  @Output() mainAssessmentUpdated: EventEmitter<boolean> = new EventEmitter()

  assessmentForm: FormGroup = this.fb.group({
    start_date: ['', Validators.required],
    end_date: ['', Validators.required],
    assessmentId: ['', Validators.required],
    video_mode: ['Preview'],
  });

  //Constructor
  constructor(
    private dataService: DataService,
    private router: Router,
    private route: ActivatedRoute,
    private confirmationService: ConfirmationService,
    public permissionService: PermissionHandlerService,
    private fb: FormBuilder
  ) { }

  ngOnChanges(simpleChange: SimpleChanges) {
    if (simpleChange['updateAssessmentData']) {
      console.log('UPDATE DATA', simpleChange['updateAssessmentData'].currentValue)
      this.updateAssessmentData = simpleChange['updateAssessmentData'].currentValue;
      if (!!this.updateAssessmentData) {
        this.assessmentForm.patchValue(this.updateAssessmentData);
        this.selectedAssessment = { ...this.updateAssessmentData, _id: this.updateAssessmentData.parentId };
      }
    }
  }

  ngOnInit() {
    this.getAssessments()

    this.assessmentForm = this.fb.group({
      start_date: [new Date(this.courseData?.start_date), Validators.required],
      end_date: [new Date(this.courseData?.end_date), Validators.required],
      assessmentId: ['', Validators.required],
      video_mode: ['Unblock'],
    });

    this.assessmentForm.get('start_date')?.valueChanges.subscribe(e => {
      this.startDate = e;
    })
  }

  assessmentUpdated(data: any) {
    this.updateAssessmentData = null;
    this.addNewAssessment = false;
    this.mainAssessmentUpdated.emit(true)
  }

  addedNewAssessmentSession() {
    //TODO:Implement this
    this.addNewAssessment = false;
    this.ngOnInit()
  }

  private async getAssessments(): Promise<void> {
    let res = await this.dataService.getData('assessment/1/1000/TAG_10?trainingTypeId=' + this.trainingTypeId);
    console.log(res.data);
    if (res.error) {
      return;
    }
    this.assessments = res?.data?.data?.map((item: any) => {
      return {
        _id: item._id,
        name: item.name,
        type: item.type,
        assessmentTitle: item.name,
        assessmentType: item.type,
        description: item.description,
        status: item.status,
        questionBankId: item.questionBankId,
        comments: item.comments,
        trainingTypeId: item.trainingTypeId,
        allowedAttempts: item.allowedAttempts,
        questions: item.questions,
        startDate: item.startDate,
        endDate: item.endDate,
        testType: item.testType,
        totalMarks: item.totalMarks
      };
    });
  }

  isFieldError(field: string) {
    const control = this.assessmentForm.get(field);
    if (control?.dirty && control.touched && control.errors?.['required']) {
      return true
    }
    return false
  }

  markAllFieldsTouchedAndDirty(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      control?.markAsTouched();
      control?.markAsDirty();
    });
  }

  async createAssessmentSession() {
    console.log('SELECTED S', this.selectedAssessment)

    if (!this.assessmentForm.valid) {
      this.markAllFieldsTouchedAndDirty(this.assessmentForm);
      return
    }
    let formValues = this.assessmentForm.getRawValue()
    this.selectedAssessment.cloneParentId = formValues.assessmentId;
    const { _id, ...assessment } = this.selectedAssessment;
    const clonedAssessmentId: any = await this.cloneAssessment(assessment);

    let assessmentSessionData = {
      "title": this.selectedAssessment.assessmentTitle,
      "date": this.today,
      "sessionType": SessionContentType.Assessment,
      "assessmentId": clonedAssessmentId,
      "description": this.selectedAssessment.description,
      "start_time": formValues.start_date,
      "end_time": formValues.end_date,
      "video_mode": formValues.video_mode,
      "parentId": _id
    }

    this.createSession!(assessmentSessionData)
    this.selectedAssessment = null;
  }

  async createSession(data: any) {
    data.courseId = this.courseId;
    console.log('SESSION CREATE CALLED', data, this.courseId)
    try {
      let method = 'POST';
      if (!!this.updateAssessmentData) {
        method = 'PUT';
        data._id = this.updateAssessmentData._id
      }
      let res = await this.dataService.postData('session', data, method)
      if (!res.error) {
        this.sessionCreated.emit(true)
        return
      }
      console.log(res.msg)
    } catch (e) {
      console.log(e)
    }
  }

  async cloneAssessment(assessment: any) {
    try {
      let res = await this.dataService.postData('assessment', assessment);
      if (res.error) {
        return;
      }
      return res.data._id;
    } catch (error) {
      console.log(error);
    }
  };

  async openAddQuestion(assessment:any) {
    let url, id;
    let queryParams: any = { courseId: this.courseId };
    url = `/assessment/add-question`
    id = assessment._id;
    queryParams['QBankId'] = assessment.questionBankId
    if (url) {
      this.router.navigate([url, id], { queryParams: queryParams })
    }
  }
}
